import { useEffect } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useRestaurantBySlug } from '../../api/getRestaurantBySlug';

const PLATFORM = 'Tablz';
const SEPARATOR = ' | ';

type Params = {
	restaurantSlug?: string;
	reservationId?: string;
	checkoutId?: string;
	step?: string;
	tableSlug?: string;
};

/**
 * Joins non-empty segments with ` | ` and always appends "Tablz".
 *
 * formatTitle('Book a Table', 'Palma Restaurant') → "Book a Table | Palma Restaurant | Tablz"
 * formatTitle('Palma Restaurant')                 → "Palma Restaurant | Tablz"
 * formatTitle()                                   → "Tablz"
 */
export function formatTitle(...segments: (string | undefined | null)[]): string {
	const parts = segments.filter(Boolean) as string[];
	if (parts.length === 0) {
		return PLATFORM;
	}
	return [...parts, PLATFORM].join(SEPARATOR);
}

/**
 * Resolves the page label from pathname.
 * Returns null when the page is the restaurant landing (exact :restaurantSlug) so the title uses the restaurant name as the label.
 * Returns undefined when the path doesn't match any mapping (use default title; the 404 page sets its own title when it mounts).
 * Check most-specific paths first.
 */
function getPageLabel(pathname: string, t: (key: string) => string): string | null | undefined {
	const segments = pathname.split('/').filter(Boolean);

	// Index
	if (pathname === '/' || segments.length === 0) {
		return t('titles.document_title_book_a_table');
	}

	// Booking flow (no restaurant in path) — most specific first
	if (pathname.startsWith('/booking/')) {
		if (pathname.endsWith('/decline')) {
			return t('titles.document_title_decline_request');
		}
		if (pathname.endsWith('/approve')) {
			return t('titles.document_title_approve_request');
		}
		if (pathname.endsWith('/cancel-success')) {
			return t('titles.document_title_cancellation');
		}
		if (pathname.includes('/modify/seating-options/details')) {
			return t('titles.document_title_review_table');
		}
		if (pathname.includes('/modify/seating-options')) {
			return t('titles.document_title_seating_options');
		}
		if (pathname.includes('/modify/guest')) {
			return t('titles.document_title_guest_details');
		}
		if (pathname.includes('/modify/addons')) {
			return t('titles.document_title_add_ons');
		}
		if (pathname.match(/^\/booking\/[^/]+\/modify\/?$/)) {
			return t('titles.document_title_change_parameters');
		}
		if (pathname.match(/^\/booking\/[^/]+\/payment$/)) {
			return t('titles.document_title_payment');
		}
		if (pathname.match(/^\/booking\/[^/]+\/refund$/)) {
			return t('titles.document_title_confirm_refund');
		}
		if (pathname.match(/^\/booking\/[^/]+$/)) {
			return t('titles.document_title_manage_booking');
		}
	}

	// Restaurant-scoped routes: require at least [restaurantSlug]
	if (segments.length >= 1) {
		const rest = pathname.slice(segments[0].length + 2) || ''; // +2 to skip leading slash after first segment

		// Exact restaurant landing: only :restaurantSlug, no trailing path
		if (rest === '') {
			return null;
		}

		// Checkout (rest e.g. "checkout/xxx/policies")
		if (rest.startsWith('checkout/')) {
			if (rest.endsWith('/confirmation')) {
				return t('titles.document_title_booking_confirmed');
			}
			if (rest.endsWith('/policies')) {
				return t('titles.document_title_policies');
			}
			if (rest.endsWith('/details')) {
				return t('titles.document_title_guest_details');
			}
			if (rest.endsWith('/payment')) {
				return t('titles.document_title_payment_details');
			}
			return t('titles.checkout');
		}

		// Explore: path ends in /explore → Table Results (rest is exactly "explore", so check before explore/)
		if (rest === 'explore') {
			return t('titles.document_title_table_results');
		}
		if (rest.startsWith('explore/')) {
			if (rest.endsWith('/availability')) {
				return t('titles.find_availability');
			}
			if (rest === 'explore/filters') {
				return t('titles.document_title_booking_parameters');
			}
			if (/^explore\/[^/]+$/.test(rest)) {
				return t('titles.document_title_table_information');
			}
		}

		if (rest === 'seating') {
			return t('titles.document_title_seating_options');
		}
	}

	// No mapping: use default title (404 page sets "Page not found" when it mounts)
	return undefined;
}

export function DocumentTitle() {
	const { pathname } = useLocation();
	const params = useParams<Params>();
	const { t } = useTranslation();
	const { restaurantSlug } = params;

	const { data: restaurant } = useRestaurantBySlug({
		slug: restaurantSlug ?? ''
	});

	const venueName = restaurant?.displayName ?? restaurant?.name;

	useEffect(() => {
		const label = getPageLabel(pathname, t);

		let fullTitle: string;
		if (label === null) {
			// Restaurant landing — venue name is the primary context
			fullTitle = formatTitle(venueName || undefined);
		}
		else if (label === undefined) {
			// Unknown route — bare platform name (404 page overrides when it mounts)
			fullTitle = PLATFORM;
		}
		else {
			fullTitle = formatTitle(label, venueName || undefined);
		}

		document.title = fullTitle;
	}, [pathname, venueName, t]);

	return null;
}
